import Fuse from "fuse.js";
import type { IntelligenceApi } from "./api";
import { AuthenticationRequiredError } from "./auth";
import type { AnalysisModelId, MapArea } from "./domain";
import type { LabelGeography } from "./map-view";

interface SearchContext {
  readonly model: AnalysisModelId;
  readonly geography: LabelGeography;
  readonly label: string;
}

const RESULT_LIMIT = 10;

export class AreaSearch {
  private readonly input: HTMLInputElement;
  private readonly panel: HTMLElement;
  private readonly list: HTMLElement;
  private readonly status: HTMLElement;
  private readonly retry: HTMLButtonElement;
  private readonly events = new AbortController();
  private readonly cache = new Map<string, Fuse<MapArea>>();
  private context: SearchContext | null = null;
  private index: Fuse<MapArea> | null = null;
  private request: AbortController | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private results: readonly MapArea[] = [];
  private activeIndex = -1;
  private open = false;
  private destroyed = false;

  public constructor(
    root: HTMLElement,
    private readonly api: Pick<IntelligenceApi, "mapAreas">,
    private readonly onSelect: (area: MapArea) => void,
    private readonly onAuthenticationRequired: (error: AuthenticationRequiredError) => void,
  ) {
    root.innerHTML = `
      <input type="search" role="combobox" aria-label="Search map labels" aria-autocomplete="list" aria-expanded="false" aria-controls="area-search-results" autocomplete="off" spellcheck="false" disabled placeholder="Choose a label type to search" />
      <div class="area-search__panel" hidden>
        <ul class="area-search__results" id="area-search-results" role="listbox" aria-label="Matching areas"></ul>
        <p class="area-search__status" role="status"></p>
        <button class="button button--white" type="button" hidden>Retry</button>
      </div>`;
    this.input = root.querySelector("input")!;
    this.panel = root.querySelector(".area-search__panel")!;
    this.list = root.querySelector("ul")!;
    this.status = root.querySelector("[role=status]")!;
    this.retry = root.querySelector("button")!;
    const options = { signal: this.events.signal };
    this.input.addEventListener("input", () => this.schedule(), options);
    this.input.addEventListener("focus", () => this.search(), options);
    this.input.addEventListener("keydown", (event) => this.keydown(event), options);
    this.list.addEventListener("pointerdown", (event) => event.preventDefault(), options);
    this.list.addEventListener(
      "click",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const option = target.closest<HTMLElement>("[data-result-index]");
        if (option !== null) this.select(Number(option.dataset.resultIndex));
      },
      options,
    );
    this.retry.addEventListener(
      "click",
      () => {
        this.input.focus();
        this.search();
      },
      options,
    );
    root.addEventListener(
      "focusout",
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !root.contains(event.relatedTarget))
          this.close();
      },
      options,
    );
    root.ownerDocument.addEventListener(
      "pointerdown",
      (event) => {
        if (event.target instanceof Node && !root.contains(event.target)) this.close();
      },
      options,
    );
  }

  public update(context: SearchContext): void {
    if (context.model === this.context?.model && context.geography === this.context.geography)
      return;
    this.request?.abort();
    this.request = null;
    this.input.removeAttribute("aria-busy");
    this.context = context;
    this.index = this.cache.get(`${context.model}:${context.geography}`) ?? null;
    this.close();
    this.clearResults();
    this.input.disabled = context.geography === "off";
    this.input.placeholder = this.input.disabled
      ? "Choose a label type to search"
      : `Search ${context.label}…`;
    this.input.setAttribute("aria-label", this.input.placeholder);
    if (this.input.disabled) this.input.value = "";
  }

  public destroy(): void {
    this.destroyed = true;
    this.close();
    this.request?.abort();
    this.events.abort();
    this.cache.clear();
  }

  private schedule(): void {
    this.close();
    this.clearResults();
    if (this.input.value.trim() === "") return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.search();
    }, 150);
  }

  private search(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    if (this.destroyed || this.input.disabled || this.input.value.trim() === "") {
      this.close();
      return;
    }
    this.open = true;
    this.panel.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
    if (this.index === null) {
      this.status.textContent = "Loading areas…";
      this.retry.hidden = true;
      if (this.request === null) void this.load();
      return;
    }
    this.clearResults();
    this.results = this.index
      .search(this.input.value.trim(), { limit: RESULT_LIMIT })
      .map(({ item }) => item);
    this.status.textContent =
      this.results.length === 0
        ? "No matching areas."
        : `${this.results.length} ${this.results.length === 1 ? "match" : "matches"}${this.results.length === RESULT_LIMIT ? " shown; refine your search for more" : ""}.`;
    for (const [index, area] of this.results.entries()) {
      const option = document.createElement("li");
      option.id = `area-search-result-${index}`;
      option.dataset.resultIndex = String(index);
      option.setAttribute("role", "option");
      option.setAttribute("aria-selected", "false");
      const name = document.createElement("span");
      name.textContent = area.name || area.code;
      const code = document.createElement("small");
      code.textContent = area.name === area.code ? "" : area.code;
      option.append(name, code);
      this.list.append(option);
    }
  }

  private async load(): Promise<void> {
    const context = this.context;
    if (context === null || context.geography === "off") return;
    const request = new AbortController();
    this.request = request;
    this.clearResults();
    this.status.textContent = "Loading areas…";
    this.input.setAttribute("aria-busy", "true");
    try {
      const { areas } = await this.api.mapAreas(
        context.model,
        context.geography,
        request.signal,
      );
      if (request.signal.aborted || this.destroyed) return;
      this.index = new Fuse(areas, {
        keys: ["name", "code"],
        threshold: 0.3,
        ignoreLocation: true,
        ignoreDiacritics: true,
        ignoreFieldNorm: true,
      });
      this.cache.set(`${context.model}:${context.geography}`, this.index);
      if (this.open) this.search();
    } catch (error) {
      if (request.signal.aborted || this.destroyed) return;
      if (error instanceof AuthenticationRequiredError) {
        this.onAuthenticationRequired(error);
        return;
      }
      this.status.textContent =
        error instanceof Error ? error.message : "Could not load areas.";
      this.retry.hidden = false;
    } finally {
      if (this.request === request) {
        this.request = null;
        this.input.removeAttribute("aria-busy");
      }
    }
  }

  private keydown(event: KeyboardEvent): void {
    if (event.isComposing) return;
    if (event.key === "Escape") {
      this.close();
      event.stopPropagation();
      event.preventDefault();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!this.open) this.search();
      if (this.results.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      this.activeIndex =
        this.activeIndex === -1
          ? step === 1
            ? 0
            : this.results.length - 1
          : (this.activeIndex + step + this.results.length) % this.results.length;
      for (const [index, option] of Array.from(this.list.children).entries()) {
        option.setAttribute("aria-selected", String(index === this.activeIndex));
        if (index === this.activeIndex) {
          this.input.setAttribute("aria-activedescendant", option.id);
          option.scrollIntoView({ block: "nearest" });
        }
      }
    } else if (event.key === "Enter" && this.open && this.results.length > 0) {
      event.preventDefault();
      this.select(Math.max(0, this.activeIndex));
    }
  }

  private select(index: number): void {
    const area = this.results[index];
    if (area === undefined) return;
    this.input.value = area.name || area.code;
    this.close();
    this.onSelect(area);
  }

  private clearResults(): void {
    this.results = [];
    this.activeIndex = -1;
    this.list.replaceChildren();
    this.status.textContent = "";
    this.retry.hidden = true;
    this.input.removeAttribute("aria-activedescendant");
  }

  private close(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.open = false;
    this.panel.hidden = true;
    this.input.setAttribute("aria-expanded", "false");
    this.input.removeAttribute("aria-activedescendant");
  }
}
